/**
 * @file AppLayout — основной layout с sidebar, header и уведомлениями
 * @see docs/tz/01-phase-mvp.md §2.1 (Layout + Router)
 * @see docs/tz/09-design-system.md §17 (Responsive)
 */
import {
  AppShell,
  NavLink,
  Group,
  Title,
  ActionIcon,
  Avatar,
  Text,
  Menu,
  Indicator,
  ScrollArea,
  Divider,
  Button,
  useMantineColorScheme,
} from '@mantine/core';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  IconDashboard,
  IconBriefcase,
  IconReport,
  IconMap,
  IconAlertTriangle,
  IconChecklist,
  IconCurrencyDollar,
  IconTarget,
  IconUsers,
  IconSettings,
  IconSun,
  IconMoon,
  IconLogout,
  IconBell,
  IconPresentation,
  IconShieldLock,
} from '@tabler/icons-react';
import { useCurrentUser, useLogout } from '@/hooks/useAuth';
import { useAppHotkeys } from '@/hooks/useHotkeys';
import { useUIStore } from '@/stores/uiStore';
import {
  useUnreadCount,
  useNotifications,
  useMarkRead,
  useMarkAllRead,
  type AppNotification,
} from '@/hooks/useNotifications';

const NAV_ITEMS = [
  { label: 'Дашборд', icon: IconDashboard, path: '/dashboard' },
  { label: 'Портфель проектов', icon: IconBriefcase, path: '/portfolio' },
  { label: 'Отчёты', icon: IconReport, path: '/reports' },
  { label: 'Дорожная карта', icon: IconMap, path: '/roadmap' },
  { label: 'Риски', icon: IconAlertTriangle, path: '/risks' },
  { label: 'Задачник', icon: IconChecklist, path: '/tasks' },
  { label: 'Финансы', icon: IconCurrencyDollar, path: '/finance' },
  { label: 'Пресейл', icon: IconTarget, path: '/presale' },
  { label: 'Загруженность', icon: IconUsers, path: '/workload' },
];

function NotificationItem({
  notification,
  onNavigate,
}: {
  notification: AppNotification;
  onNavigate: (n: AppNotification) => void;
}) {
  const markRead = useMarkRead();

  const handleClick = () => {
    if (!notification.read) {
      markRead.mutate(notification._id);
    }
    onNavigate(notification);
  };

  return (
    <Menu.Item
      onClick={handleClick}
      style={{ opacity: notification.read ? 0.6 : 1 }}
    >
      <Text size="sm" fw={notification.read ? 400 : 600} lineClamp={1}>
        {notification.title}
      </Text>
      <Text size="xs" c="dimmed" lineClamp={1}>
        {notification.message}
      </Text>
      <Text size="xs" c="dimmed" mt={2}>
        {new Date(notification.createdAt).toLocaleString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </Text>
    </Menu.Item>
  );
}

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: user } = useCurrentUser();
  const logout = useLogout();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const presentationMode = useUIStore((s) => s.presentationMode);
  const togglePresentation = useUIStore((s) => s.togglePresentation);

  // Регистрация глобальных хоткеев
  useAppHotkeys();

  const { data: unreadCount = 0 } = useUnreadCount();
  const { data: notifications = [] } = useNotifications();
  const markAllRead = useMarkAllRead();

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((w) => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '??';

  const handleNotificationNavigate = (n: AppNotification) => {
    if (n.entityType && n.entityId) {
      switch (n.entityType) {
        case 'project':
          navigate(`/projects/${n.entityId}`);
          break;
        case 'task':
          navigate('/tasks');
          break;
        case 'presale':
          navigate('/presale');
          break;
        default:
          break;
      }
    }
  };

  return (
    <AppShell
      navbar={presentationMode ? undefined : { width: 260, breakpoint: 'md' }}
      header={{ height: 60 }}
      padding="md"
      style={presentationMode ? { fontSize: '110%' } : undefined}
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="xs">
            <IconShieldLock size={24} color="var(--mantine-color-blue-6)" />
            <Title order={4}>МУЛЬТИСЕРВИС</Title>
          </Group>
          <Group>
            {/* Уведомления — колокольчик (скрыт в режиме презентации) */}
            {!presentationMode && <Menu shadow="md" width={360} position="bottom-end">
              <Menu.Target>
                <Indicator
                  label={unreadCount > 0 ? unreadCount : undefined}
                  size={16}
                  disabled={unreadCount === 0}
                  color="red"
                >
                  <ActionIcon variant="subtle" title="Уведомления">
                    <IconBell size={18} />
                  </ActionIcon>
                </Indicator>
              </Menu.Target>
              <Menu.Dropdown>
                <Group px="sm" py="xs" justify="space-between">
                  <Text size="sm" fw={600}>Уведомления</Text>
                  {unreadCount > 0 && (
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      onClick={() => markAllRead.mutate()}
                    >
                      Прочитать все
                    </Button>
                  )}
                </Group>
                <Divider />
                <ScrollArea.Autosize mah={400}>
                  {notifications.length === 0 && (
                    <Text size="xs" c="dimmed" ta="center" py="xl">
                      Нет уведомлений
                    </Text>
                  )}
                  {notifications.map(n => (
                    <NotificationItem
                      key={n._id}
                      notification={n}
                      onNavigate={handleNotificationNavigate}
                    />
                  ))}
                </ScrollArea.Autosize>
              </Menu.Dropdown>
            </Menu>}

            {/* Режим презентации */}
            <ActionIcon
              variant={presentationMode ? 'filled' : 'subtle'}
              color={presentationMode ? 'blue' : undefined}
              onClick={togglePresentation}
              title="Режим презентации (Ctrl+Shift+P)"
            >
              <IconPresentation size={18} />
            </ActionIcon>

            {/* Тема */}
            <ActionIcon
              variant="subtle"
              onClick={() => toggleColorScheme()}
              title="Сменить тему"
            >
              {colorScheme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
            </ActionIcon>

            {/* Профиль */}
            <Menu shadow="md" width={200}>
              <Menu.Target>
                <Avatar size={32} radius="xl" color="blue" style={{ cursor: 'pointer' }}>
                  {initials}
                </Avatar>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>
                  <Text size="sm" fw={500}>{user?.name}</Text>
                  <Text size="xs" c="dimmed">{user?.email}</Text>
                </Menu.Label>
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconLogout size={14} />}
                  color="red"
                  onClick={() => logout.mutate()}
                >
                  Выход
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      {!presentationMode && (
        <AppShell.Navbar p="xs">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              label={item.label}
              leftSection={<item.icon size={20} stroke={1.5} />}
              active={location.pathname === item.path || (item.path !== '/portfolio' && location.pathname.startsWith(item.path))}
              onClick={() => navigate(item.path)}
              style={{ borderRadius: 'var(--mantine-radius-md)' }}
            />
          ))}
          <div style={{ flex: 1 }} />
          <Divider my="xs" />
          <NavLink
            label="Настройки"
            leftSection={<IconSettings size={20} stroke={1.5} />}
            active={location.pathname === '/settings'}
            onClick={() => navigate('/settings')}
            style={{ borderRadius: 'var(--mantine-radius-md)' }}
          />
        </AppShell.Navbar>
      )}

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
